<template>
  <section
    class="tw-h-[100svh]"
    :style="`color: ${vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text-secondary)'}`"
  >
    <!-- Desktop video -->
    <video
      class="tw-absolute tw-left-0 tw-top-0 tw-hidden tw-h-full tw-w-full tw-object-cover lg:tw-block"
      autoplay
      loop
      muted
      playsinline
    >
      <source :src="vars.backgroundVideo" type="video/mp4" />
    </video>

    <!-- Mobile video -->
    <video
      class="tw-absolute tw-left-0 tw-top-0 tw-block tw-h-full tw-w-full tw-object-cover lg:tw-hidden"
      autoplay
      loop
      muted
      playsinline
    >
      <source :src="vars.mobileBackgroundVideo" type="video/mp4" />
    </video>

    <!-- v1  -->
    <div v-if="variants.v1 === variant" class="tw-h-full tw-w-full tw-text-center">
      <div class="tw-absolute tw-inset-0 tw-bg-black tw-opacity-50"></div>
      <div class="tw-relative tw-z-10 tw-flex tw-h-full tw-items-end tw-justify-center">
        <div
          class="tw-z-100 tw-mx-2 tw-mb-6 tw-flex tw-flex-col tw-gap-2 tw-text-center tw-text-white md:tw-mx-12 md:tw-mb-8 xl:tw-mb-24 xl:tw-gap-6"
        >
          <h2>
            {{ vars.titleText }}
          </h2>
          <h5 class="tw-mt-4 tw-text-white">
            {{ vars.descriptionText }}
          </h5>
          <div class="tw-m-6 tw-flex tw-items-center tw-justify-center tw-gap-3 lg:tw-gap-5">
            <nuxt-link
              :external="true"
              :to="button.link"
              v-for="(button, index) in buttons"
              :key="index"
              :class="`n-${button.type}`"
            >
              {{ button.label }}
            </nuxt-link>
          </div>
        </div>
      </div>
    </div>

    <!-- v3  -->
    <div v-else-if="variants.v3 === variant" class="tw-h-full tw-w-full tw-text-center">
      <div class="tw-absolute tw-inset-0 tw-bg-black tw-opacity-50"></div>
      <div class="tw-relative tw-z-10 tw-flex tw-h-full tw-items-end tw-justify-center">
        <div
          class="tw-z-100 tw-mx-2 tw-mb-6 tw-flex tw-flex-col tw-gap-2 tw-text-center tw-text-white md:tw-mx-12 md:tw-mb-8 xl:tw-mb-24 xl:tw-gap-6"
        >
          <h1 v-if="vars.titleText && !vars.titleHtml" class="tw-text-white">
            {{ vars.titleText }}
          </h1>
          <h1 v-else v-html="vars.titleHtml" class="tw-text-white"></h1>

          <div class="tw-m-6 tw-flex tw-items-center tw-justify-center tw-gap-3 lg:tw-gap-5">
            <nuxt-link
              :external="true"
              :to="button.link"
              v-for="(button, index) in buttons"
              :key="index"
              :class="`n-${button.type}`"
            >
              {{ button.label }}
            </nuxt-link>
          </div>
        </div>
      </div>
    </div>

    <!-- v2  -->
    <span
      v-if="variants.v2 === variant && vars.watermarkImage"
      class="watermark tw-pointer-events-none tw-absolute -tw-bottom-20 -tw-right-32 tw-z-0 tw-hidden lg:tw-block"
    >
      <img :src="vars.watermarkImage" alt="watermark" class="tw-w-[600px] xl:tw-w-[800px] 2xl:tw-w-[1000px]" />
    </span>
    <div v-if="variants.v2 === variant" class="tw-h-full tw-w-full tw-text-center">
      <div class="tw-absolute tw-inset-0 tw-bg-black tw-opacity-30"></div>
      <div class="tw-relative tw-z-10 tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center">
        <div
          class="tw-z-100 tw-mx-2 tw-mb-4 tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-text-center tw-text-white"
        >
          <div v-html="vars.titleHtml" class="tw-mb-5"></div>
          <small v-if="vars.descriptionText" class="tw-mb-4 tw-max-w-2xl tw-text-white">
            {{ vars.descriptionText }}
          </small>
          <div class="tw-flex tw-w-full tw-justify-center tw-gap-3 tw-text-center max-sm:tw-flex-col lg:tw-gap-5">
            <nuxt-link
              :external="true"
              :target="isExternalLink(button.link) ? '_blank' : '_self'"
              :to="button.link"
              v-for="(button, index) in buttons"
              :key="index"
              :class="`n-${button.type}`"
            >
              {{ button.label }}
            </nuxt-link>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="variants.v4 === variant" class="tw-h-full tw-w-full tw-text-start">
      <div class="tw-absolute tw-inset-0 tw-bg-black tw-opacity-50"></div>
      <div class="tw-relative tw-z-10 tw-flex tw-h-full tw-items-end tw-justify-start">
        <div
          class="tw-z-100 tw-mx-2 tw-mb-6 tw-flex tw-flex-col tw-gap-2 tw-text-start tw-text-white md:tw-mx-12 md:tw-mb-8 xl:tw-mb-24 xl:tw-gap-6"
        >
          <h1>
            {{ vars.titleText }}
          </h1>

          <div class="tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row lg:tw-gap-5">
            <nuxt-link
              :external="true"
              :to="button.link"
              v-for="(button, index) in buttons"
              :key="index"
              :class="`n-${button.type} tw-w-full lg:tw-w-auto`"
            >
              {{ button.label }}
            </nuxt-link>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { ComponentMixin } from '~/mixins/component.mixin';

export default defineNuxtComponent({
  name: 'HeroSection',

  mixins: [ComponentMixin],

  data() {
    return {
      variants: {
        v1: 'v1',
        v2: 'v2',
        v3: 'v3',
        v4: 'v4',
      },
    };
  },

  computed: {
    buttons() {
      return this.groupedVariables.buttons;
    },
  },

  methods: {
    isExternalLink(link: string) {
      return link.includes('http');
    },
  },
});
</script>
